import { useContext } from 'react'

import { faLock, faPen } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Checkbox, FormControlLabel, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CoreEmptyState } from '@scoir/empty-state'
import { Pill } from '@scoir/pills'

import features from 'common/college-features'
import SYSTEM_PERMISSIONS from 'common/system-permissions'
import useCollegeFeatures from 'common/use-college-features'
import useIsShadow from 'common/use-is-shadow'
import { useAppSelector } from 'store/index'
import { hasPermission } from 'store/user'

import IntegrationConfigurationContext from '../IntegrationConfigurationProvider/Context'
import CampusESPConfig from './CampusESPConfig'

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(3),
  },
  labelAndPill: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: theme.spacing(2, 0),
  },
  settingContainer: {
    backgroundColor: theme.palette.pageBackground?.main,
    padding: theme.spacing(1),
    margin: theme.spacing(2, 0),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkBox: {
    padding: theme.spacing(1),
    margin: 0,
  },
}))

const ParentHandraisers = () => {
  const classes = useStyles()
  const { state, onSendParentHandraisersBeforeStudent } = useContext(IntegrationConfigurationContext)

  // Send Parent Handraisers before student handraisers
  const isShadow = useIsShadow()
  const isParentHandraisersEnabled = useCollegeFeatures(features.SUPPORTS_PARENT_HANDRAISERS)
  const canManageParentHandraisers = useAppSelector((state) =>
    hasPermission(state, SYSTEM_PERMISSIONS.CAN_VIEW_SHADOW_FEATURES),
  )
  const canEnableParentHandraisers = isParentHandraisersEnabled && (isShadow || canManageParentHandraisers)

  // Configure CampusESP
  const isCampusESPEnabled = useCollegeFeatures(features.SUPPORTS_CAMPUS_ESP)
  const canManageHandraisers = useAppSelector((state) =>
    hasPermission(state, SYSTEM_PERMISSIONS.VIEW_STUDENT_INTERESTS),
  )

  const canManageCampusESP = isCampusESPEnabled && (isShadow || canManageHandraisers)

  const showParentHandraisers = canEnableParentHandraisers || canManageCampusESP

  if (isCampusESPEnabled && !canManageCampusESP) {
    return (
      <>
        <Typography variant="h6">Parent Handraisers</Typography>
        <CoreEmptyState
          condensed
          icon={faPen}
          title="You don’t have permission to manage Parent Handraisers"
          subTitle="Contact your school’s administrator to grant you the appropriate access."
        />
      </>
    )
  }

  if (!showParentHandraisers) {
    return null
  }

  return (
    <Box className={classes.paper}>
      <Box className={classes.labelAndPill}>
        <Typography variant="h6">Parent Handraisers</Typography>
      </Box>
      {canManageCampusESP && <CampusESPConfig />}
      {canEnableParentHandraisers && (
        <Box className={classes.settingContainer}>
          <FormControlLabel
            className={classes.checkBox}
            control={
              <Checkbox
                color="primary"
                checked={state.SendParentHandraisersBeforeStudent}
                disableRipple={false}
                onChange={(e) => onSendParentHandraisersBeforeStudent({ checked: e.target.checked })}
              />
            }
            label={
              <Box>
                <Typography>Send parent handraisers before student handraisers</Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  Internal setting — not visible to school users.
                </Typography>
              </Box>
            }
          />
          <Pill
            className={classes.labelAndPill}
            color="secondary"
          >
            <FontAwesomeIcon icon={faLock} />
            SCOIR ADMIN ONLY
          </Pill>
        </Box>
      )}
    </Box>
  )
}

export default ParentHandraisers
